import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// @import Custom Components
import { WriterlyAuthLayout } from '@shared/Auth/Layout';
import { WriterlyAuthLogo } from '@shared/Auth/Logo';
import { WriterlyAuthForm } from '@shared/Auth/Form';
import { WriterlyBackgroundLoader } from '@shared/Loader/Background';

// @import Mui Components
import { Typography, Button } from '@mui/material';

// @import Reducers
import { login } from '@redux/slices/auth';

// @import Paths
import { authPaths, guestPaths } from '@routes/paths';

// @import Utils
import { formFields, formValidation, formPage } from '@pages/auth/login/utils';
import { selectRedirectTo, setRedirectTo } from '@redux/slices/user';

const AuthLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.auth.login.loading);
  const accessToken = useSelector((state) => state.user.accessToken);
  const redirectTo = useSelector(selectRedirectTo);

  useEffect(() => {
    if (accessToken) {
      const redirectPath = redirectTo || authPaths.home;
      navigate(redirectPath);
      dispatch(setRedirectTo(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      navigate(authPaths.home);
    }
  }, [navigate]);

  const loginForm = () => {
    const handleSubmit = async (values, actions) => {
      let resetValues = {};
      try {
        await dispatch(login({ credentials: values })).unwrap();
      } catch (error) {
        resetValues = { ...values, password: '' };
      }
      actions.resetForm({ values: resetValues });
    };

    return (
      <div className="flex flex-col h-full w-full xl:w-1/2 2xl:w-2/6">
        <WriterlyBackgroundLoader visible={loading} />
        <div className="form mt-20 flex flex-col gap-5 text-center lg:mt-32">
          <WriterlyAuthLogo color="white" />
          <div className="title flex justify-center mt-10">
            <Typography variant="h2">{t('login.title')}</Typography>
          </div>
          <div className="inputs flex flex-col px-28 justify-center mt-10">
            <WriterlyAuthForm
              formFields={formFields}
              validationSchema={formValidation}
              handleSubmit={handleSubmit}
              formPage={formPage}
              extraFields={
                <Typography
                  variant="b3"
                  className="font-bold uppercase hover:underline cursor-pointer flex justify-end "
                  onClick={() => navigate('/password-recovery')}
                  data-testid={'login-passwordRecovery'}
                >
                  {t('forgotPassword')}
                </Typography>
              }
              buttons={
                <Button
                  variant="contained"
                  size="large"
                  className="mt-5 !border-none"
                  type="submit"
                >
                  {t('continue')}
                </Button>
              }
            />
          </div>
        </div>
        <div className="flex items-center justify-center mt-5">
          <Typography variant="b3" className="font-bold uppercase mr-1">
            {t('requestAccess')}
          </Typography>
          <Typography
            variant="b3"
            className="font-bold uppercase underline cursor-pointer"
            data-testid={'login-createAccount'}
            onClick={() => navigate(guestPaths.signUp)}
          >
            {t('signUp.title')}
          </Typography>
        </div>
      </div>
    );
  };

  return <WriterlyAuthLayout leftContent={loginForm()} />;
};

AuthLogin.propTypes = {
  data: PropTypes.any,
};

export default AuthLogin;
